import { apiFetch } from '../config/api'

export type ReportRegisterPayload = {
  reportId: string
  name: string
  tag: string
  fileKey: string
}

export async function createReportUploadUrl(params: {
  projectId: string
  pipeSetId: string
  fileName: string
  contentType: string
}): Promise<{ uploadUrl: string; fileKey: string; reportId: string }> {
  const path = `/api/projects/${params.projectId}/pipe-sets/${params.pipeSetId}/reports/upload-url`
  return apiFetch(path, {
    method: 'POST',
    body: JSON.stringify({ fileName: params.fileName, contentType: params.contentType })
  })
}

export async function putFileToPresignedUrl(uploadUrl: string, file: File): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type || 'application/octet-stream'
    }
  })
  if (!res.ok) {
    throw new Error(`Upload failed (${res.status})`)
  }
}

export async function registerReportMetadata(params: {
  projectId: string
  pipeSetId: string
  body: ReportRegisterPayload
}): Promise<ReportRegisterPayload> {
  const path = `/api/projects/${params.projectId}/pipe-sets/${params.pipeSetId}/reports`
  return apiFetch(path, {
    method: 'POST',
    body: JSON.stringify(params.body)
  })
}

export async function getReportDownloadUrl(params: {
  projectId: string
  pipeSetId: string
  reportId: string
}): Promise<{ downloadUrl: string }> {
  const rid = encodeURIComponent(params.reportId)
  const path = `/api/projects/${params.projectId}/pipe-sets/${params.pipeSetId}/reports/${rid}/download-url`
  return apiFetch(path)
}

export async function deleteReport(params: {
  projectId: string
  pipeSetId: string
  reportId: string
}): Promise<void> {
  const rid = encodeURIComponent(params.reportId)
  const path = `/api/projects/${params.projectId}/pipe-sets/${params.pipeSetId}/reports/${rid}`
  await apiFetch(path, { method: 'DELETE' })
}

/**
 * Provenance stamped by the server from the verified Cognito JWT context
 * (`req.userId` + `req.userEmail`). Optional because legacy report rows
 * pre-date this field; the UI renders an em-dash placeholder when absent.
 *
 * Added in `pqr-report-history-and-rev` slice 01. Mirrors the server-side
 * `ReportGeneratedBy` contract.
 */
export interface ReportGeneratedBy {
  id: string
  email: string
}

export interface ReportListItem {
  id: string
  name: string
  tag: string
  fileKey: string
  uploadedAt: string
  updatedAt?: string
  deletedAt?: string
  /**
   * Optional structured metadata added in
   * `pqr-report-history-and-rev` (slice 01). New PQRs always carry
   * these; legacy rows have them filled best-effort by the server-side
   * `parseLegacyReportName` reader (stored wins over parsed).
   * `generatedBy` is `undefined` for any report row that pre-dates the
   * provenance change.
   */
  weldId?: string
  weldIds?: string[]
  pqrNumber?: string
  revision?: string
  generatedBy?: ReportGeneratedBy
  /**
   * Stable per-bevel scope id for WPS reports — mirrors `weldId`'s role
   * on PQR rows. Added in `wps-history-bevel-scoped` (issue 03). Always
   * absent on PQR rows and on legacy WPS rows generated before the
   * server-side round-trip landed.
   */
  bevelId?: string
  /**
   * Per-bevel WPS Number stamped at generation time for WPS reports.
   * Mirrors `pqrNumber`'s role on PQR rows. Optional: legacy rows lack
   * it and consumers must handle the absent case.
   */
  wpsNumber?: string
  /**
   * Source PQR number(s) when the WPS was generated from selected PQR
   * trains. New WPS rows carry this; legacy bevel-scoped rows omit it.
   */
  pqrNumbers?: string[]
  /** PDF-import placeholder row with no real S3 object yet. */
  importDraft?: boolean
  /**
   * Extraction id of the originating PDF import. Lets the client fetch the
   * real source PDF (scoped to WPS or PQR pages) for imported rows.
   */
  extractionId?: string
}

export interface ReportsListResponse {
  items: ReportListItem[]
  tagOptions: string[]
}

export async function listReports(params: {
  projectId: string
  pipeSetId: string
}): Promise<ReportsListResponse> {
  const path = `/api/projects/${params.projectId}/pipe-sets/${params.pipeSetId}/reports`
  return apiFetch(path)
}

/** Snapshot of a person stored on `sections.pqr.{preparedBy,reviewedBy}`. */
export interface GeneratePqrPerson {
  id: string
  name: string
  title?: string
  date?: string
  signature?: string
  stampImage?: string
  signedAt?: string
  memberId?: string
}

/**
 * Sparse Materials patch from the editable PQR preview. Each entry targets
 * exactly one row of the Materials table (`lotIndex` × `pipeIndex`) and only
 * includes the cells the user actually changed.
 */
export interface GeneratePqrMaterialEntry {
  lotIndex: number
  pipeIndex: number
  materialSpec?: string
  typeGrade?: string
  pipeManufacturer?: string
  heatNumber?: string
}

export type GeneratePqrJointParameterKey =
  | 'parameter1'
  | 'parameter2'
  | 'parameter3'
  | 'parameter4'
  | 'parameter5'
  | 'parameter6'
  | 'parameter7'
  | 'parameter8'
  | 'parameter9'
  | 'parameter10'
  | 'parameter11'
  | 'parameter12'
  | 'parameter13'

export interface GeneratePqrJointPreparation {
  bevelIndex: number
  profileIndex: number
  parameters: Partial<Record<GeneratePqrJointParameterKey, string>>
}

export interface GeneratePqrWeldingVariablesEntry {
  passLabel: string
  bevelIndex: number
  fields: Record<string, string>
}

export type GeneratePqrWeldingParametersColumnKey =
  | 'preheat'
  | 'amperage'
  | 'voltage'
  | 'wfs'
  | 'travel'
  | 'heatInput'

export interface GeneratePqrWeldingParametersOverrideEntry {
  passLabel: string
  cells: Partial<
    Record<GeneratePqrWeldingParametersColumnKey, { min: string; max: string }>
  >
}

export interface GeneratePqrNotesOverrides {
  welders?: Array<{ role: string; name: string }> | null
  equipment?: Array<{ label: string; value: string }> | null
}

/**
 * Editable Save (& optional Generate) payload for the PQR preview.
 *
 * `generatePdf`:
 *   - `true` (default, omit): persist + render PDF + upload to S3. Returns
 *     `GeneratePqrFullResponse`. Identifiers / signatories are required.
 *   - `false`: persist-only "Save" — writes `wps_sections` (incl. all section
 *     patches) and returns only the new `sectionsVersion`. Identifiers /
 *     signatories may be blank (draft save).
 */
export interface GeneratePqrRequest {
  pqrNumber: string
  revision: string
  preparedBy: GeneratePqrPerson
  reviewedBy: GeneratePqrPerson
  /**
   * Optional after slice 03 of `pqr-preview-field-navigation`. The
   * Comments cell on the PQR canvas now mirrors the picked weld's
   * FloorSheet `operators.notes` textarea and is no longer client-side
   * staged, so the Save payload does not need to round-trip it. The
   * server keeps the legacy field for backwards compatibility and
   * defaults to an empty string when absent.
   */
  comments?: string
  materials?: GeneratePqrMaterialEntry[]
  jointPreparation?: GeneratePqrJointPreparation
  weldingVariables?: GeneratePqrWeldingVariablesEntry[]
  weldingParametersOverrides?: GeneratePqrWeldingParametersOverrideEntry[]
  notesOverrides?: GeneratePqrNotesOverrides
  /** `null` on first save; otherwise the `sectionsVersion` last seen. */
  ifMatchVersion: number | null
  /** Defaults to `true`. Pass `false` for the persist-only "Save" path. */
  generatePdf?: boolean
  /** Optional recipients for user notification email (generatePdf only). */
  notificationEmails?: string[]
  /**
   * Optional per-weld scope (Phase 2 of `pqr-five-changes`). When set,
   * the generated PDF scopes Materials / Joint Prep / Pass Sequence /
   * Welding Parameters / Welders to the picked weld and adds a
   * `-Weld-<id>` suffix to the file name. Omitted = legacy aggregated
   * PQR. The server silently downgrades unknown ids to the aggregated
   * view, so a stale URL parameter never blocks generation.
   */
  weldId?: string
  weldIds?: string[]
  /** Floor Sheet Recorded By signatories keyed by weld id (1 person or 1..4). */
  recordedByByWeldId?: Record<string, GeneratePqrPerson | GeneratePqrPerson[]>
  /** Floor Sheet Reviewed By signatory keyed by weld id (optional). */
  reviewedByByWeldId?: Record<string, GeneratePqrPerson>
}

/**
 * Server response. `report` is present only when the request generated a
 * PDF (`generatePdf` was true or omitted). The `sectionsVersion` is always
 * populated and the client must capture it as the next `ifMatchVersion`.
 */
export interface GeneratePqrResponse {
  sectionsVersion: number
  report?: {
    reportId: string
    name: string
    fileKey: string
    downloadUrl: string
  }
}

/**
 * Convenience response type for the PDF-generating path — exposes the
 * report fields at the top level (matches the on-the-wire shape the server
 * has used since `pqr-editable-preview`).
 */
export interface GeneratePqrServerResponse {
  sectionsVersion: number
  reportId?: string
  name?: string
  fileKey?: string
  downloadUrl?: string
}

/**
 * POST /reports/generate-pqr — normalises the server's two response shapes
 * into a single `GeneratePqrResponse` so callers do not branch on
 * `generatePdf`. Callers that need the report fields can read
 * `response.report`.
 */
export async function generatePqrReport(params: {
  projectId: string
  pipeSetId: string
  body: GeneratePqrRequest
}): Promise<GeneratePqrResponse> {
  const path = `/api/projects/${params.projectId}/pipe-sets/${params.pipeSetId}/reports/generate-pqr`
  const raw = (await apiFetch(path, {
    method: 'POST',
    body: JSON.stringify(params.body)
  })) as GeneratePqrServerResponse

  const generatePdf = params.body.generatePdf !== false
  if (!generatePdf) {
    return { sectionsVersion: raw.sectionsVersion }
  }
  // Defensive: even when the server returns a flat shape, surface it as
  // a nested `report` so consumers can rely on a stable contract.
  return {
    sectionsVersion: raw.sectionsVersion,
    report:
      raw.reportId && raw.name && raw.fileKey && raw.downloadUrl
        ? {
            reportId: raw.reportId,
            name: raw.name,
            fileKey: raw.fileKey,
            downloadUrl: raw.downloadUrl
          }
        : undefined
  }
}

// ---------------------------------------------------------------------------
// Slice 02 (WPS PDF tracer bullet) — generate-wps client helper.
//
// Mirrors `generatePqrReport` so the two endpoints can be maintained
// in parallel. Tracer-bullet body is intentionally narrower than
// `GeneratePqrRequest`: only the header / approvals / notes fields
// the WPS template currently renders. Subsequent slices (03..07) add
// per-section patches (materials, joint prep, welding variables, etc.)
// using the same pattern as PQR.
// ---------------------------------------------------------------------------

/** One signatory card on the WPS PDF's 3-column approvals footer. */
export interface GenerateWpsApproval {
  /** Canonical contractor/endClient/crc or any extra imported/custom slot id. */
  id: string
  /** Mirrored org header from PDF import (e.g. "Troy Construction"). */
  label?: string
  name: string
  title?: string
  date?: string
  signature?: string
  stampImage?: string
  signedAt?: string
  memberId?: string
}

/** Numbered free-form note row (rendered in slice 06; persisted now). */
export interface GenerateWpsNote {
  id: string
  body: string
}

/**
 * Editable Save (& optional Generate) payload for the WPS preview.
 *
 * `generatePdf`:
 *   - `true` (default, omit): persist + render PDF + upload to S3. Returns
 *     `GenerateWpsResponse` with `.report` populated. `wpsNumber` +
 *     `revision` are required.
 *   - `false`: persist-only "Save" — writes `sections.wps` and returns only
 *     the new `sectionsVersion`. `wpsNumber` / `revision` may be blank
 *     (draft save).
 */
export interface GenerateWpsRequest {
  wpsNumber: string
  revision: string
  application?: string
  equipmentDescription?: string
  approvals?: GenerateWpsApproval[]
  notes?: GenerateWpsNote[]
  /** `null` on first save; otherwise the `sectionsVersion` last seen. */
  ifMatchVersion: number | null
  /** Defaults to `true`. Pass `false` for the persist-only "Save" path. */
  generatePdf?: boolean
  /** Optional recipients for user notification email (generatePdf only). */
  notificationEmails?: string[]
  /**
   * Stable per-bevel scope id (legacy bevel-scoped WPS). New PQR-
   * scoped WPS rows use `weldIds` + `pqrNumbers` instead.
   */
  bevelId?: string
  /**
   * Union of weld ids from selected PQR train(s). Scopes PDF content
   * via floorsheet `bevelIndex` resolution (same as PQR).
   */
  weldIds?: string[]
  /** PQR number(s) the user selected when generating this WPS. */
  pqrNumbers?: string[]
}

/**
 * Server response. `report` is present only when the request generated
 * a PDF (`generatePdf` was true or omitted). The `sectionsVersion` is
 * always populated and the client must capture it as the next
 * `ifMatchVersion`.
 */
export interface GenerateWpsResponse {
  sectionsVersion: number
  report?: {
    reportId: string
    name: string
    fileKey: string
    downloadUrl: string
  }
}

interface GenerateWpsServerResponse {
  sectionsVersion: number
  reportId?: string
  name?: string
  fileKey?: string
  downloadUrl?: string
}

/**
 * POST /reports/generate-wps — normalises the server's two response
 * shapes into a single `GenerateWpsResponse` so callers do not branch
 * on `generatePdf`. Callers that need the report fields can read
 * `response.report`.
 */
export async function generateWpsReport(params: {
  projectId: string
  pipeSetId: string
  body: GenerateWpsRequest
}): Promise<GenerateWpsResponse> {
  const path = `/api/projects/${params.projectId}/pipe-sets/${params.pipeSetId}/reports/generate-wps`
  const raw = (await apiFetch(path, {
    method: 'POST',
    body: JSON.stringify(params.body)
  })) as GenerateWpsServerResponse

  const generatePdf = params.body.generatePdf !== false
  if (!generatePdf) {
    return { sectionsVersion: raw.sectionsVersion }
  }
  return {
    sectionsVersion: raw.sectionsVersion,
    report:
      raw.reportId && raw.name && raw.fileKey && raw.downloadUrl
        ? {
            reportId: raw.reportId,
            name: raw.name,
            fileKey: raw.fileKey,
            downloadUrl: raw.downloadUrl
          }
        : undefined
  }
}

// ---------------------------------------------------------------------------
// WPS Limits Excel export (pixel-faithful server workbook)
// ---------------------------------------------------------------------------

export interface ExportWpsLimitsExcelFile {
  name: string
  fileKey: string
  downloadUrl: string
}

export interface ExportWpsLimitsExcelResponse {
  name: string
  fileKey: string
  downloadUrl: string
  files?: ExportWpsLimitsExcelFile[]
}

export async function exportWpsLimitsExcel(params: {
  projectId: string
  pipeSetId: string
  body: {
    weldId?: string
    weldIds?: string[]
    boundary?: number
  }
}): Promise<ExportWpsLimitsExcelResponse> {
  const path = `/api/projects/${params.projectId}/pipe-sets/${params.pipeSetId}/reports/export-wps-limits-excel`
  return apiFetch(path, {
    method: 'POST',
    body: JSON.stringify(params.body)
  })
}
